/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import analyticsAPI from '@api/analytics';
import { kpisRequest, kpisSuccess, kpisFailure } from '@store/reducers/dashboard/kpis';
import {
  usageEvolutionRequest,
  usageEvolutionSuccess,
  usageEvolutionFailure,
} from '@store/reducers/dashboard/usageEvolution';
import { flowsUsageRequest, flowsUsageSuccess, flowsUsageFailure } from '@store/reducers/dashboard/flowsUsage';
import {
  getDialogGraphRequest,
  getDialogGraphSuccess,
  getDialogGraphFailure,
} from '@store/reducers/dashboard/dialogGraphs/getDialogGraph';
import {
  corpusPerformanceRequest,
  corpusPerformanceSuccess,
  corpusPerformanceFailure,
} from '@store/reducers/dashboard/corpusPerformance';
import { topIntentsSuccess } from '@store/reducers/dashboard/topIntents';
import { errorAlert } from '@store/reducers/alerts';
import { filterDropdown, resetDropdown } from '@store/reducers/dashboard/dialogGraphs/filterDropdown';
import { graphPositionsUpdated } from '@store/reducers/dashboard/dialogGraphs/graphPositionsUpdated';
import {
  filterDateDropdownUpdateID,
  filterDateDropdownUpdateDesc,
  filterDateDropdownNeedsUpdate,
  filterDateDropdownUpdateRange,
} from '@store/reducers/dashboard/filterDateDropdown';
import { useGetFilteredAnalytics } from '@components/nlp-portal/content/dashboard/hooks/useGetFilteredAnalytics';
import { getFilterById } from '@utils/dashboard/filters';

function getUsageEvolution(agentId, query) {
  return dispatch => {
    dispatch(usageEvolutionRequest());
    analyticsAPI
      .getUsageEvolution(agentId, query)
      .then(response => {
        dispatch(usageEvolutionSuccess(response.data));
      })
      .catch(error => {
        dispatch(usageEvolutionFailure(error.message));
        dispatch(errorAlert((error.response && error.response.data) || error.message));
      });
  };
}

function getFlowsUsage(agentId, query) {
  return dispatch => {
    dispatch(flowsUsageRequest());

    analyticsAPI
      .getFlowsUsage(agentId, query)
      .then(response => {
        dispatch(flowsUsageSuccess(response.data));
      })
      .catch(error => {
        dispatch(flowsUsageFailure(error.message));
        dispatch(errorAlert((error.response && error.response.data) || error.message));
      });
  };
}

function getDialogGraph(agentId, query) {
  return dispatch => {
    dispatch(getDialogGraphRequest());
    analyticsAPI
      .getDialogGraph(agentId, query)
      .then(response => {
        dispatch(getDialogGraphSuccess(response.data));
      })
      .catch(error => {
        dispatch(getDialogGraphFailure(error.message));
        dispatch(errorAlert((error.response && error.response.data) || error.message));
      });
  };
}

function getCorpusPerformance(agentId, query) {
  return dispatch => {
    dispatch(corpusPerformanceRequest());
    analyticsAPI
      .getCorpusPerformance(agentId, query)
      .then(response => {
        dispatch(corpusPerformanceSuccess(response.data));
      })
      .catch(error => {
        dispatch(corpusPerformanceFailure(error.message));
        dispatch(errorAlert((error.response && error.response.data) || error.message));
      });
  };
}

function getKpis(agentId, query) {
  return dispatch => {
    dispatch(kpisRequest());
    analyticsAPI
      .getKpis(agentId, query)
      .then(response => {
        dispatch(kpisSuccess(response.data.kpis));
        dispatch(
          topIntentsSuccess(
            response.data.topUsedIntents.map(intentBundle => {
              if (intentBundle.onlyBot) {
                intentBundle.intent += ` (only bot)`;
              }
              return intentBundle;
            })
          )
        );
      })
      .catch(error => {
        dispatch(kpisFailure(error.message));
        dispatch(errorAlert((error.response && error.response.data) || error.message));
      });
  };
}

function updatedFilterDropdown(value) {
  return dispatch => {
    dispatch(filterDropdown(value));
  };
}
function resetFilterDropdown() {
  return dispatch => {
    dispatch(resetDropdown());
  };
}

function setGraphPositionsUpdated(value) {
  return dispatch => {
    dispatch(graphPositionsUpdated(value));
  };
}

function updateTimeFrameAnalytics(id, desc, needUpdate) {
  useGetFilteredAnalytics(id);
  return dispatch => {
    dispatch(filterDateDropdownUpdateID(id));
    dispatch(filterDateDropdownUpdateDesc(desc));
    dispatch(filterDateDropdownNeedsUpdate(needUpdate));
  };
}

function updateDateFilter(value) {
  return dispatch => {
    const today = new Date();
    const filter = getFilterById(value);
    const { fromDate, toDate } = { ...filter.options(today) };
    const query = { fromDate, toDate };
    dispatch(filterDateDropdownUpdateRange(query));
  };
}

export default {
  getUsageEvolution,
  getFlowsUsage,
  getDialogGraph,
  getCorpusPerformance,
  getKpis,
  updatedFilterDropdown,
  setGraphPositionsUpdated,
  updateTimeFrameAnalytics,
  updateDateFilter,
  resetFilterDropdown,
};
